import { useEffect, useRef, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { GoogleAdsNotice } from '@/components/google-ads/GoogleAdsShell';
import { errMsg } from '@/components/google-ads/youtube/youtubeShared';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import {
  googleAdsService,
  GoogleAdsAccount,
  GoogleAdsUserList,
} from '@/services/google-ads.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { Users, Plus, Upload, Globe, Info, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

type Props = {
  /** Rendered inside a TabbedPage shell — suppress this page's own Topbar. */
  embedded?: boolean;
};

export default function GoogleAdsAudiences({ embedded = false }: Props = {}) {
  const [accounts, setAccounts] = useState<GoogleAdsAccount[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadVersion = useRef(0);
  const [accountUid, setAccountUid] = useState<string>('');
  const [audiences, setAudiences] = useState<GoogleAdsUserList[] | null>(null);
  const [groups, setGroups] = useState<ContactGroup[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [groupUid, setGroupUid] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    googleAdsService.list()
      .then((accs) => {
        accs = accs.filter(a => a.customerId && !a.isManager && a.isActive);
        setAccounts(accs);
        const def = accs.find((a) => a.isDefault) ?? accs[0];
        if (def) setAccountUid(def.accountUid);
      })
      .catch(e => setLoadError(errMsg(e)))
      .finally(() => setAccountsLoading(false));
    contactGroupsService.list().then(setGroups).catch(() => setGroups([]));
  }, []);

  const refresh = async (uid: string) => {
    if (!uid) return;
    const version = ++loadVersion.current;
    setLoadError(null);
    setAudiences(null);
    try {
      const r = await googleAdsService.listAudiences(uid);
      if (version === loadVersion.current) setAudiences(r);
    } catch (e) {
      if (version === loadVersion.current) setLoadError(errMsg(e));
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (accountUid) refresh(accountUid); }, [accountUid]);

  const create = async () => {
    if (!accountUid) { toast.error('Pick an account'); return; }
    if (!groupUid) { toast.error('Pick a contact group'); return; }
    if (!name.trim()) { toast.error('Audience name is required'); return; }
    setBusy(true);
    try {
      const r = await googleAdsService.createAudienceFromGroup(accountUid, {
        groupUid,
        name: name.trim(),
        description: description.trim() || undefined,
      });
      toast.success(
        `Customer Match list created · ${r.uploaded} contacts uploaded. Match results take 24–48h on Google's side.`,
      );
      setCreateOpen(false);
      setGroupUid(''); setName(''); setDescription('');
      await refresh(accountUid);
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setBusy(false); }
  };

  if (accountsLoading) return <div className="skeleton h-52 rounded-2xl" aria-label="Loading accounts" />;
  if (loadError && !accounts.length) return <GoogleAdsNotice title="Unable to load accounts" error>{loadError}</GoogleAdsNotice>;
  if (accounts.length === 0) {
    return (
      <>
        {!embedded && <Topbar title="Google Ads Audiences" breadcrumb="Google Ads" />}
        <div className={clsx('gads-embedded w-full px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
          <EmptyState
            icon={<Globe size={40} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Customer Match needs a connected, customer-attached Google Ads account."
            action={
              <a href="/google-ads/account">
                <Button variant="primary"><ExternalLink size={14} /> Go to Google Ads Account</Button>
              </a>
            }
          />
        </div>
      </>
    );
  }

  // Account switcher and per-page actions. The tabbed shell hides this
  // page's Topbar, so the same controls render above the content there.
  const headerActions = (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {accounts.length > 1 && (
          <Select aria-label="Advertising account" value={accountUid} onChange={(e) => setAccountUid(e.target.value)} className="!h-9 !text-xs">
            {accounts.map((a) => (
              <option key={a.accountUid} value={a.accountUid}>
                {a.customerName ?? a.googleEmail} {a.customerId ? `(${a.customerId})` : ''}
              </option>
            ))}
          </Select>
        )}
        <Button variant="primary" className="gads-primary" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Create from group
        </Button>
      </div>
  );

  return (
    <>
      {!embedded && (
        <Topbar
        title="Google Ads Audiences"
        breadcrumb="Google Ads"
        description="Customer Match — upload hashed phone + email from your CRM contact groups."
        right={headerActions}
        />
      )}

      <div className={clsx('gads-embedded w-full px-7 space-y-4', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {embedded && <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><div><h3 className="gads-panel-title">Audiences</h3><p className="gads-muted mt-1">Build Customer Match audiences from your CRM contact groups.</p></div>{headerActions}</div>}
        {loadError && <GoogleAdsNotice title="Unable to load audiences" error>{loadError} <button className="ml-2 underline" onClick={() => refresh(accountUid)}>Try again</button></GoogleAdsNotice>}
        {/* Honest note about lookalikes */}
        <div className="flex items-start gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-3">
          <Info size={15} className="mt-0.5 shrink-0 text-primary" />
          <div className="text-xs text-text-secondary leading-relaxed">
            <strong>Use your customer audiences across campaigns.</strong> This workspace manages Customer Match lists. Google Ads offers additional audience tools, including Demand Gen lookalikes and optimized targeting, according to campaign eligibility.
          </div>
        </div>

        <Card padded={false} className="overflow-hidden">
          {audiences === null && !loadError ? (
            <div className="space-y-2 p-5">
              {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}
            </div>
          ) : audiences === null ? null : audiences.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <Users size={24} />
              </div>
              <div>
                <div className="font-semibold text-text-primary">No Customer Match lists yet</div>
                <div className="mt-1 text-sm text-text-muted">
                  Pick a contact group and click <strong>Create from group</strong> to upload one.
                </div>
              </div>
              <Button variant="primary" className="gads-primary" onClick={() => setCreateOpen(true)}>
                <Plus size={14} /> Create from group
              </Button>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-[2.5fr_1fr_1fr_1fr] gap-4 border-b border-border px-5 py-3 text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">
                <span>Audience</span>
                <span>Display reach</span>
                <span>Search reach</span>
                <span>Match rate</span>
              </div>
              {audiences.map((a) => (
                <div
                  key={a.id}
                  className="grid grid-cols-[2.5fr_1fr_1fr_1fr] gap-4 items-center border-b border-border px-5 py-4 last:border-b-0 hover:bg-surface-alt transition-colors"
                >
                  <div>
                    <div className="font-semibold text-text-primary truncate">{a.name}</div>
                    <div className="mt-0.5 font-mono text-xs text-text-muted">ID {a.id}</div>
                    {a.description && (
                      <div className="mt-0.5 text-xs text-text-muted line-clamp-1">{a.description}</div>
                    )}
                  </div>
                  <div className="tabular-nums font-bold text-text-primary">
                    {a.sizeForDisplay?.toLocaleString() ?? '—'}
                  </div>
                  <div className="tabular-nums font-bold text-text-primary">
                    {a.sizeForSearch?.toLocaleString() ?? '—'}
                  </div>
                  <div className="text-xs">
                    {a.matchRatePercentage != null ? (
                      <span className="font-bold text-text-primary">{a.matchRatePercentage}%</span>
                    ) : (
                      <span className="text-text-muted">building…</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <Modal
        open={createOpen}
        onClose={() => !busy && setCreateOpen(false)}
        title="Create Customer Match list from contact group"
        widthClass="max-w-lg"
      >
        <div className="px-6 py-5 space-y-3">
          <p className="text-xs text-text-muted">
            Phone (E.164) and email from each contact in the selected group are SHA-256 hashed locally and
            uploaded to Google Ads. PII never leaves Slightor in plaintext.
          </p>

          <FormField label="Contact group" required>
            <Select value={groupUid} onChange={(e) => setGroupUid(e.target.value)}>
              <option value="">— Select a group —</option>
              {groups.map((g) => (
                <option key={g.groupUid} value={g.groupUid}>
                  {g.name} ({g.contactCount ?? 0} contacts)
                </option>
              ))}
            </Select>
          </FormField>

          <FormField label="Audience name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="UAE customers — past 90 days" />
          </FormField>

          <FormField label="Description (optional)">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What is this audience for?" />
          </FormField>

          <div className="rounded-lg border border-primary/30 bg-primary-soft px-3 py-2 text-2xs text-info">
            Tip: Customer Match needs at least <strong>1,000 matched users</strong> before Google starts
            serving ads to the list. If your group is small, build it from a wider win/lead window.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={create} loading={busy} disabled={busy || !groupUid || !name.trim()}>
            <Upload size={14} /> Create + Upload
          </Button>
        </div>
      </Modal>
    </>
  );
}
